<template>
	<template v-if="!isError">
		<div
			v-if="isLoading || (searchStore?.pageData?.results?.length ?? 0) > 0"
			class="ProgrammeCards"
		>
			<template
				v-for="(item, index) in searchResultsWithBanners"
				:key="itemKey(item, index)"
			>
				<ClientOnly v-if="isBannerClusterDescriptor(item)">
					<AdBannerCluster
						:bannerIds="(item as BannerClusterDescriptor).bannerIds"
						:bannerType="(item as BannerClusterDescriptor).bannerType"
						:cssClass="(item as BannerClusterDescriptor).cssClass"
					/>
				</ClientOnly>

				<ClientOnly v-else-if="isBannerDescriptor(item)">
					<AdBanner
						:containerId="(item as BannerDescriptor).containerId"
						:bannerType="(item as BannerDescriptor).bannerType"
						:cssClass="(item as BannerDescriptor).cssClass"
					/>
				</ClientOnly>

				<ProgrammeCard
					v-else
					:programme="(item as IPersonalizedProgrammeCard | undefined)"
					:loading="isLoading"
					:position="index"
					:externalServicesLoading="externalServicesLoading"
				/>
			</template>
		</div>
		<NoResultsRecommendations/>
		<Pagination />
		<Faqs />
	</template>

	<ErrorResult v-else/>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import ProgrammeCard from './ProgrammeCard/ProgrammeCard.vue';
import { useSearchStore } from '@stores/useSearchStore';
import { useEventAggregationDependencies } from '@composables/useEventAggregationDependencies';
import type IPersonalizedProgrammeCard from '@shared/interfaces/dtos/IPersonalizedProgrammeCard';
import { EVENT_TYPES } from '@app/interfaces/useEventAggregation.interface';
import Faqs from '../Faqs/Faqs.vue';
import Pagination from '../Pagination/Pagination.vue';
import { useEventSender } from '@composables/useEventSender';
import { PortalType } from '@studyportals/domain-client';
import NoResultsRecommendations from '@app/components/SearchResults/LowResults/LowResultsRecommendation/LowResultsRecommendations.vue';
import ErrorResult from '@app/components/ErrorResult/ErrorResult.vue';
import AdBanner from '@app/ad-manager/components/AdBanner.vue';
import AdBannerCluster from '@app/ad-manager/components/AdBannerCluster.vue';
import {
	type BannerDescriptor,
	type BannerClusterDescriptor,
	type SearchResultItem,
	isBannerDescriptor,
	isBannerClusterDescriptor,
	interleaveResultsWithBanners,
} from '@app/ad-manager/utils/banner-positions';

const searchStore = useSearchStore();

const isError = computed(() => searchStore.pageData?.isError ?? false);
const { isReady } = useEventAggregationDependencies({
	waitFor: [EVENT_TYPES.BestfitOverviewReady, EVENT_TYPES.WishlistServiceReady],
	waitForAll: true,
	onReady: () => searchStore.setExternalServicesAsLoaded()
});

const skeletonCardCount = 20;

const programmes = computed<(IPersonalizedProgrammeCard | undefined)[]>(() => {
	if (searchStore.loading || !searchStore.pageData?.results) {
		return Array.from({ length: skeletonCardCount }, () => undefined);
	}

	return searchStore.pageData.results;
});

const isLoading = computed(() => searchStore.loading);
const externalServicesLoading = computed(() => !searchStore.externalServicesLoaded || searchStore.loading);

const searchResultsWithBanners = computed<SearchResultItem<IPersonalizedProgrammeCard | undefined>[]>(() => {
	const results = programmes.value;

	if (isLoading.value || results.length === 0) {
		return results;
	}

	const totalPages = searchStore.currentPage;
	const { merged } = interleaveResultsWithBanners(results, totalPages);

	return merged;
});

const itemKey = (item: SearchResultItem<IPersonalizedProgrammeCard | undefined>, index: number): string => {
	if (isBannerClusterDescriptor(item)) return `cluster-${(item as BannerClusterDescriptor).bannerIds.join('-')}`;
	if (isBannerDescriptor(item)) return `banner-${(item as BannerDescriptor).containerId}`;
	const programme = item as IPersonalizedProgrammeCard | undefined;
	return programme?.id ?? `skeleton-${index}`;
};

const lastExternalServicesKey = ref<string | null>(null);
const externalServicesKey = computed(() => {
	const results = searchStore.pageData?.results ?? [];
	const ids = results.map((programme) => programme?.id ?? '').join('|');
	return `${searchStore.searchType}-${searchStore.currentPage}-${ids}`;
});

const initExternalServices = async (): Promise<void> => {
	if (!isReady.value || searchStore.loading) return;

	const results = searchStore.pageData?.results ?? [];
	if (results.length === 0) return;

	if (externalServicesKey.value === lastExternalServicesKey.value) return;
	lastExternalServicesKey.value = externalServicesKey.value;

	await nextTick();

	useEventSender().sendEvents(searchStore.portalType as PortalType);
	searchStore.setExternalServicesAsLoaded();
};

if (import.meta.client) {
	watch(
		[externalServicesKey, () => searchStore.loading, isReady],
		() => {
			void initExternalServices();
		},
		{ deep: false, flush: 'post' }
	);
}

</script>

<style scoped lang="scss">
	.ProgrammeCards {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
</style>
